import React, { createElement, Component } from 'react';
import { Polyline, Marker, Tooltip, Popup } from 'react-leaflet';
import { Icon } from 'leaflet';
import { FuelIcon, RadioIcon, ShieldIcon, FlagIcon } from 'lucide-react';
import { convoyRouteSegments, alternativeRouteSegment, pointsOfInterest, routeColors } from './convoyData';
// Create custom icons for POIs
const createCustomIcon = (iconComponent: JSX.Element, color: string) => {
  // Create a DOM element for the custom icon
  const iconHtml = document.createElement('div');
  iconHtml.className = `bg-${color}-100 p-1 rounded-full border-2 border-${color}-600 flex items-center justify-center`;
  iconHtml.style.backgroundColor = color === 'blue' ? '#DBEAFE' : color === 'green' ? '#D1FAE5' : color === 'red' ? '#FEE2E2' : color === 'amber' ? '#FEF3C7' : '#E0E7FF';
  iconHtml.style.borderColor = color === 'blue' ? '#2563EB' : color === 'green' ? '#059669' : color === 'red' ? '#DC2626' : color === 'amber' ? '#D97706' : '#4F46E5';
  return new Icon({
    iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41]
  });
};
const getIconForPOI = (iconType: string) => {
  switch (iconType) {
    case 'fuel':
      return createCustomIcon(<FuelIcon className="w-4 h-4" data-id="element-199" />, 'green');
    case 'radio':
      return createCustomIcon(<RadioIcon className="w-4 h-4" data-id="element-200" />, 'blue');
    case 'shield':
      return createCustomIcon(<ShieldIcon className="w-4 h-4" data-id="element-201" />, 'red');
    case 'flag':
      return createCustomIcon(<FlagIcon className="w-4 h-4" data-id="element-202" />, 'amber');
    default:
      return createCustomIcon(<FuelIcon className="w-4 h-4" data-id="element-203" />, 'blue');
  }
};
// Get the full name for POI based on ID
const getFullNameForPOI = (poiId: string): string => {
  switch (poiId) {
    case 'frpa':
    case 'frpa-alt':
      return 'Forward Resupply Point';
    case 'cnb':
      return 'Coordination Node';
    case 'scp-c':
      return 'Security Control Point';
    case 'acg':
      return 'Arrival Control Group';
    default:
      return 'Point of Interest';
  }
};
interface ConvoyLayerProps {
  selectedTimePoint: string;
  useAlternativeRoute?: boolean;
}
export const ConvoyLayer: React.FC<ConvoyLayerProps> = ({
  selectedTimePoint,
  useAlternativeRoute = false
}) => {
  // Determine which days to show based on selected time point
  const getMaxDayToShow = () => {
    switch (selectedTimePoint) {
      case '24h':
        return 1;
      case '48h':
        return 2;
      case '72h':
        return 3;
      case '96h':
        return 4;
      case '5d':
        return 5;
      case '6d':
        return 6;
      case '7d':
        return 7;
      default:
        return 0;
      // For "Now", show all as future (dotted)
    }
  };
  const maxDay = getMaxDayToShow();
  const isNowSelected = selectedTimePoint === 'now';
  return <>
      {/* Render route segments */}
      {convoyRouteSegments.filter(segment => {
      // For day 1, check if we should use the alternative route
      if (segment.day === 1 && useAlternativeRoute) {
        return false;
      }
      return true; // Show all segments, but style them differently
    }).map(segment => {
      // Determine if this segment is in the future based on the selected time point
      const isFutureSegment = segment.day > maxDay || isNowSelected;
      return <Polyline key={segment.id} positions={segment.coordinates} pathOptions={{
        color: routeColors.default,
        weight: 4,
        opacity: isFutureSegment ? 0.6 : 0.8,
        dashArray: isFutureSegment ? '5, 10' : undefined
      }} data-id="element-204">
              <Popup data-id="element-205">
                <div data-id="element-206">
                  <h3 className="font-bold" data-id="element-207">{segment.name}</h3>
                  <p className="text-sm" data-id="element-208">{segment.timeframe}</p>
                  <p className="text-sm mt-1" data-id="element-209">{segment.description}</p>
                  {isFutureSegment && <p className="text-sm mt-1 text-blue-600 italic" data-id="element-210">
                      Future movement - not yet reached
                    </p>}
                </div>
              </Popup>
            </Polyline>;
    })}
      {/* Render alternative route if selected */}
      {useAlternativeRoute && <Polyline positions={alternativeRouteSegment.coordinates} pathOptions={{
      color: routeColors.alternate,
      weight: 4,
      opacity: maxDay >= 1 && !isNowSelected ? 0.8 : 0.6,
      dashArray: maxDay >= 1 && !isNowSelected ? '5, 5' : '5, 10'
    }} data-id="element-211">
          <Popup data-id="element-212">
            <div data-id="element-213">
              <h3 className="font-bold" data-id="element-214">{alternativeRouteSegment.name}</h3>
              <p className="text-sm" data-id="element-215">{alternativeRouteSegment.timeframe}</p>
              <p className="text-sm mt-1" data-id="element-216">
                {alternativeRouteSegment.description}
              </p>
              {(maxDay < 1 || isNowSelected) && <p className="text-sm mt-1 text-purple-600 italic" data-id="element-217">
                  Future movement - not yet reached
                </p>}
            </div>
          </Popup>
        </Polyline>}
      {/* Render POIs */}
      {pointsOfInterest.filter(poi => {
      // For FRPA, check if we should use the alternative location
      if (poi.id === 'frpa' && useAlternativeRoute) {
        return false;
      }
      if (poi.id === 'frpa-alt' && !useAlternativeRoute) {
        return false;
      }
      return true; // Show all POIs, but style them differently
    }).map(poi => {
      // Determine if this POI is in the future based on the selected time point
      const isFuturePOI = poi.day > maxDay || isNowSelected;
      const fullName = getFullNameForPOI(poi.id);
      return <Marker key={poi.id} position={poi.coordinates} icon={getIconForPOI(poi.icon)} opacity={isFuturePOI ? 0.6 : 1} data-id="element-218">
              <Popup data-id="element-219">
                <div data-id="element-220">
                  <h3 className="font-bold" data-id="element-221">{poi.name}</h3>
                  <p className="text-sm" data-id="element-222">Location: {poi.location}</p>
                  <p className="text-sm" data-id="element-223">Code: {poi.code}</p>
                  <p className="text-sm mt-1" data-id="element-224">{poi.tooltip}</p>
                  {isFuturePOI && <p className="text-sm mt-1 text-blue-600 italic" data-id="element-225">
                      Future point - not yet reached
                    </p>}
                </div>
              </Popup>
            </Marker>;
    })}
    </>;
};